/**
 * SyncManager - Handles offline change queue and conflict resolution
 *
 * Provides:
 * - Change tracking with queue persistence
 * - Offline detection and automatic sync on reconnect
 * - Conflict detection and notification
 * - Retry logic with exponential backoff
 */

import { API_BASE_URL, getAuthToken } from '../../../api/client';
import timelineService from '../../timeline/services/timelineService';

// Storage keys
const QUEUE_KEY = 'serviceTimerSyncQueue';
const SYNC_STATUS_KEY = 'serviceTimerSyncStatus';

// Change statuses
export const ChangeStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  CONFLICT: 'conflict',
  FAILED: 'failed',
} as const;

export type ChangeStatusType = typeof ChangeStatus[keyof typeof ChangeStatus];

// Sync statuses
export const SyncStatus = {
  SYNCED: 'synced',
  PENDING: 'pending',
  SYNCING: 'syncing',
  OFFLINE: 'offline',
  DISCONNECTED: 'disconnected',  // Network online but WebSocket disconnected
  CONFLICT: 'conflict',
} as const;

export type SyncStatusType = typeof SyncStatus[keyof typeof SyncStatus];

/**
 * A change recorded for syncing
 */
export interface SyncChange {
  id: string;
  timelineId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  timestamp: number;
  status: ChangeStatusType;
  retryCount: number;
  lastUpdatedAt?: string | null;
  errorMessage?: string | null;
  syncedAt?: number;
  serverValue?: unknown;
  serverUpdatedAt?: string;
}

/**
 * Conflict record for UI display
 */
export interface SyncConflict extends SyncChange {
  serverValue: unknown;
  serverUpdatedAt: string;
}

/**
 * Sync state for listeners
 */
export interface SyncState {
  status: SyncStatusType;
  pendingCount: number;
  isOnline: boolean;
  isWebSocketConnected: boolean;
  isSyncing: boolean;
  recentConflicts: SyncConflict[];
  lastSyncTime: number | null;
}

/**
 * Parameters for recording a change
 */
export interface RecordChangeParams {
  timelineId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  lastUpdatedAt?: string | null;
}

/**
 * Sync listener callback
 */
export type SyncListener = (state: SyncState) => void;

/**
 * Sync API result
 */
interface SyncResult {
  changeId: string;
  status: 'synced' | 'conflict' | 'error';
  serverValue?: unknown;
  serverUpdatedAt?: string;
  error?: string;
}

/**
 * Generate a UUID for change tracking
 */
const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * SyncManager singleton
 */
class SyncManager {
  private queue: SyncChange[] = [];
  private listeners: Set<SyncListener> = new Set();
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private isWebSocketConnected: boolean = false;
  private isSyncing: boolean = false;
  private recentConflicts: SyncConflict[] = [];
  private lastSyncTime: number | null = null;
  private retryTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

  constructor() {
    // Load persisted state
    this._loadFromStorage();

    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._handleOnline());
      window.addEventListener('offline', () => this._handleOffline());
    }

    // Listen for WebSocket connection status changes
    this._setupWebSocketMonitoring();
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatusType {
    if (!this.isOnline) return SyncStatus.OFFLINE;
    if (!this.isWebSocketConnected) return SyncStatus.DISCONNECTED;
    if (this.isSyncing) return SyncStatus.SYNCING;
    if (this.recentConflicts.length > 0) return SyncStatus.CONFLICT;
    if (this.queue.some(c => c.status === ChangeStatus.PENDING)) return SyncStatus.PENDING;
    return SyncStatus.SYNCED;
  }

  /**
   * Get pending change count
   */
  getPendingCount(): number {
    return this.queue.filter(c => c.status === ChangeStatus.PENDING || c.status === ChangeStatus.SYNCING).length;
  }

  /**
   * Get recent conflicts
   */
  getRecentConflicts(): SyncConflict[] {
    return [...this.recentConflicts];
  }

  /**
   * Record a change to be synced
   */
  recordChange({ timelineId, field, oldValue, newValue, lastUpdatedAt }: RecordChangeParams): string {
    const change: SyncChange = {
      id: generateId(),
      timelineId,
      field,
      oldValue,
      newValue,
      timestamp: Date.now(),
      status: ChangeStatus.PENDING,
      retryCount: 0,
      lastUpdatedAt,
      errorMessage: null,
    };

    // Add to queue
    this.queue.push(change);
    this._persist();
    this._notifyListeners();

    // Attempt to sync if online
    if (this.isOnline) {
      this._processQueue();
    }

    return change.id;
  }

  /**
   * Subscribe to sync state changes
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this._getState());
    return () => this.listeners.delete(listener);
  }

  /**
   * Clear a conflict from the list
   */
  clearConflict(changeId: string): void {
    this.recentConflicts = this.recentConflicts.filter(c => c.id !== changeId);
    this._persist();
    this._notifyListeners();
  }

  /**
   * Clear all conflicts
   */
  clearAllConflicts(): void {
    this.recentConflicts = [];
    this._persist();
    this._notifyListeners();
  }

  /**
   * Manually trigger a sync
   */
  async sync(): Promise<void> {
    if (!this.isOnline) {
      console.log('Cannot sync: offline');
      return;
    }
    await this._processQueue();
  }

  // --- Private methods ---

  private _getState(): SyncState {
    return {
      status: this.getStatus(),
      pendingCount: this.getPendingCount(),
      isOnline: this.isOnline,
      isWebSocketConnected: this.isWebSocketConnected,
      isSyncing: this.isSyncing,
      recentConflicts: this.recentConflicts,
      lastSyncTime: this.lastSyncTime,
    };
  }

  private _setupWebSocketMonitoring(): void {
    timelineService.subscribeToConnectionStatus((connected: boolean) => {
      const wasConnected = this.isWebSocketConnected;
      this.isWebSocketConnected = connected;
      if (connected && !wasConnected) {
        console.log('WebSocket connected - sync indicator updated');
      } else if (!connected && wasConnected) {
        console.log('WebSocket disconnected - sync indicator updated');
      }
      this._notifyListeners();
    });
  }

  private _notifyListeners(): void {
    const state = this._getState();
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  private _handleOnline(): void {
    console.log('Network online - processing sync queue');
    this.isOnline = true;
    this._notifyListeners();
    this._processQueue();
  }

  private _handleOffline(): void {
    console.log('Network offline - changes will be queued');
    this.isOnline = false;
    this.isSyncing = false;
    this._notifyListeners();
  }

  private async _processQueue(): Promise<void> {
    if (this.isSyncing || !this.isOnline) return;

    const pendingChanges = this.queue.filter(c => c.status === ChangeStatus.PENDING);
    if (pendingChanges.length === 0) return;

    this.isSyncing = true;
    this._notifyListeners();

    const token = getAuthToken();
    if (!token) {
      console.log('No auth token - cannot sync');
      this.isSyncing = false;
      this._notifyListeners();
      return;
    }

    try {
      // Prepare changes for batch sync
      const changesToSync = pendingChanges.map(change => ({
        id: change.id,
        timelineId: change.timelineId,
        field: change.field,
        newValue: change.newValue,
        ifUnmodifiedSince: change.lastUpdatedAt,
      }));

      // Mark as syncing
      pendingChanges.forEach(change => {
        change.status = ChangeStatus.SYNCING;
      });
      this._persist();
      this._notifyListeners();

      // Call batch sync endpoint
      const response = await fetch(`${API_BASE_URL}/timelines/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ changes: changesToSync }),
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status}`);
      }

      const { results } = await response.json() as { results: SyncResult[] };

      // Process results
      for (const result of results) {
        const change = this.queue.find(c => c.id === result.changeId);
        if (!change) continue;

        if (result.status === 'synced') {
          change.status = ChangeStatus.SYNCED;
          change.syncedAt = Date.now();
        } else if (result.status === 'conflict') {
          change.status = ChangeStatus.CONFLICT;
          change.serverValue = result.serverValue;
          change.serverUpdatedAt = result.serverUpdatedAt;
          // Add to recent conflicts for UI notification
          this.recentConflicts.push({
            ...change,
            serverValue: result.serverValue!,
            serverUpdatedAt: result.serverUpdatedAt!,
          });
        } else if (result.status === 'error') {
          change.status = ChangeStatus.FAILED;
          change.errorMessage = result.error || null;
          change.retryCount++;
          // Retry later if not too many attempts
          if (change.retryCount < 3) {
            change.status = ChangeStatus.PENDING;
            this._scheduleRetry(change);
          }
        }
      }

      // Remove synced changes from queue
      this.queue = this.queue.filter(c => c.status !== ChangeStatus.SYNCED);
      this.lastSyncTime = Date.now();
      this._persist();

    } catch (error) {
      console.error('Sync error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      // Check if it's a network error
      if (!navigator.onLine || errorMessage.includes('Failed to fetch')) {
        this._handleOffline();
      } else {
        // Mark changes as pending for retry
        pendingChanges.forEach(change => {
          change.status = ChangeStatus.PENDING;
          change.retryCount++;
          if (change.retryCount < 3) {
            this._scheduleRetry(change);
          } else {
            change.status = ChangeStatus.FAILED;
            change.errorMessage = errorMessage;
          }
        });
        this._persist();
      }
    } finally {
      this.isSyncing = false;
      this._notifyListeners();
    }
  }

  private _scheduleRetry(change: SyncChange): void {
    // Exponential backoff: 2s, 4s, 8s
    const delay = Math.pow(2, change.retryCount) * 1000;

    // Clear existing timeout if any
    const existingTimeout = this.retryTimeouts.get(change.id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      this.retryTimeouts.delete(change.id);
      if (this.isOnline && change.status === ChangeStatus.PENDING) {
        this._processQueue();
      }
    }, delay);

    this.retryTimeouts.set(change.id, timeout);
  }

  private _persist(): void {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
      localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify({
        recentConflicts: this.recentConflicts,
        lastSyncTime: this.lastSyncTime,
      }));
    } catch (error) {
      console.error('Failed to persist sync state:', error);
    }
  }

  private _loadFromStorage(): void {
    try {
      const queueData = localStorage.getItem(QUEUE_KEY);
      if (queueData) {
        this.queue = JSON.parse(queueData);
        // Reset any syncing status to pending (in case app was closed mid-sync)
        this.queue.forEach(change => {
          if (change.status === ChangeStatus.SYNCING) {
            change.status = ChangeStatus.PENDING;
          }
        });
      }

      const statusData = localStorage.getItem(SYNC_STATUS_KEY);
      if (statusData) {
        const status = JSON.parse(statusData);
        this.recentConflicts = status.recentConflicts || [];
        this.lastSyncTime = status.lastSyncTime;
        // Filter out old conflicts (older than 24 hours)
        const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
        this.recentConflicts = this.recentConflicts.filter(c => c.timestamp > dayAgo);
      }
    } catch (error) {
      console.error('Failed to load sync state:', error);
      this.queue = [];
      this.recentConflicts = [];
    }
  }
}

// Export singleton instance
const syncManager = new SyncManager();
export default syncManager;
